import '@Global/modal';
import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {createRoot} from 'react-dom/client';
import App from '@Components/app';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {useInitAiPromptBox} from '@Components/ai-prompt-box/hooks/useInitAiPromptBox';
import {AICHAT_MARKETING_PROMPT_BOX_ID} from '@Components/mystuff-hero/components/mystuff-prompt-box/mystuff-prompt-box.library';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {checkDailyLimit, onAiPromptBoxSubmit} from '@Libraries/ai-chat.library';
import {getDefaultAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {AiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';
import {GA4EventName, GA4EventParamName, AiChatStartSource} from '@Libraries/ga-events';

function MarketingAiPromptBox(): ReactElement {
  const {aiPromptBox, fetchedCredits} = useInitAiPromptBox(AICHAT_MARKETING_PROMPT_BOX_ID);
  const {actions} = aiPromptBox;

  useEffect(() => {
    void checkDailyLimit(actions.update);
    actions.update({size: AiPromptBoxSize.SMALL_EXTENDED});
  }, []);

  const generate = async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_START, {[GA4EventParamName.SOURCE]: AiChatStartSource.AI_MARKETING_PAGE});
    await onAiPromptBoxSubmit(aiPromptBox, fetchedCredits, []);
  };

  return (
    <div style={{maxWidth: 780, margin: '0 auto', zIndex: 1000, position: 'relative'}} className="_full-width spacing-m-b-6">
      <AiPromptBox
        id={AICHAT_MARKETING_PROMPT_BOX_ID}
        onGenerate={generate}
        initialConfig={{
          prompt: '',
          size: AiPromptBoxSize.SMALL_EXTENDED,
          placeholder: window.i18next.t('pmwjs_describe_the_design_you_want_to_create_elipses'),
          promptBoxState: PromptBoxState.IDLE,
          attachments: getDefaultAttachments(),
          source: AiChatSource.AI_MARKETING_PAGE,
          transcription: {showMic: true},
        }}
        onRegenerate={generate}
        options={[AiPromptBoxUploads]}
        onFocus={() => {
          void checkDailyLimit(actions.update);
        }}
      />
    </div>
  );
}

const container = document.getElementById('marketing-ai-chat-prompt-box');
if (container) {
  const root = createRoot(container);
  root.render(
    <App>
      <MarketingAiPromptBox />
    </App>
  );
}
